import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  collection, 
  addDoc, 
  serverTimestamp, 
  doc, 
  getDoc 
} from 'firebase/firestore';
// Import Cloudinary service
import { uploadImageToCloudinary, uploadAudioToCloudinary } from '../../utils/cloudinaryService';
import { db, collections } from '../../firebase.marketplace';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  FormHelperText, 
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Divider,
  InputAdornment,
  Chip,
  Container,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  styled
} from '@mui/material';
import { 
  CloudUpload as UploadIcon, 
  Save as SaveIcon, 
  ArrowBack as BackIcon,
  AddPhotoAlternate as PhotoIcon,
  Category as CategoryIcon,
  AttachMoney as MoneyIcon,
  Description as DescriptionIcon,
  Info as InfoIcon,
  MusicNote as MusicIcon
} from '@mui/icons-material';
import { FaMusic } from 'react-icons/fa';

// Styled components for image upload
const VisuallyHiddenInput = styled('input')({  
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const ImagePreviewBox = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 300,
  border: `2px dashed ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.grey[100],
  '&:hover': {
    backgroundColor: theme.palette.primary.lighter || '#e3f2fd',
  }
}));

const StyledImagePreview = styled('img')({  
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

// Product categories
const PRODUCT_CATEGORIES = [
  { value: 'music', label: 'Music' },
  { value: 'merchandise', label: 'Merchandise' },
  { value: 'artwork', label: 'Artwork' },
  { value: 'digital', label: 'Digital Content' },
  { value: 'tickets', label: 'Event Tickets' },
  { value: 'other', label: 'Other' }
];

// Platform fee percentage
const PLATFORM_FEE_PERCENTAGE = 5;

const ProductCreateForm = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    stock: '',
    tags: [],
    isDigitalDownload: false,
    audioFileUrl: ''
  });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [audioFileName, setAudioFileName] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [tagInput, setTagInput] = useState('');
  
  // Create a ref to store the upload task
  const uploadTask = useRef(null);
  
  // Artist info
  const [artistName, setArtistName] = useState('');
  
  useEffect(() => {
    // Check if user is logged in and is an artist
    if (!currentUser) {
      navigate('/marketplace/auth/signin');
      return;
    }
    
    // Fetch artist info
    const fetchArtistInfo = async () => {
      try {
        const artistDoc = await getDoc(doc(db, collections.USERS, currentUser.uid));
        if (artistDoc.exists()) {
          setArtistName(artistDoc.data().displayName || currentUser.displayName);
        } else {
          // If user document doesn't exist, use display name from auth
          setArtistName(currentUser.displayName || 'Artist');
        }
      } catch (error) {
        console.error('Error fetching artist info:', error);
        // Use display name from auth as fallback
        setArtistName(currentUser.displayName || 'Artist');
      }
    };
    
    fetchArtistInfo();
  }, [currentUser, navigate]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a valid image file (JPEG, PNG, or WebP)');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }
    
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setError(null);
  };
  
  // Handle audio file selection
  const handleAudioSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['audio/mpeg', 'audio/mp3'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a valid MP3 file');
      return;
    }
    
    // Validate file size (max 20MB)
    if (file.size > 20 * 1024 * 1024) {
      setError('Audio file size should be less than 20MB');
      return;
    }
    
    setAudioFile(file);
    setAudioFileName(file.name);
    setError(null);
  };
  
  // Handle tag input
  const handleTagInputChange = (e) => {
    setTagInput(e.target.value);
  };
  
  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      
      // Don't add duplicate tags
      if (!formData.tags.includes(tagInput.trim())) {
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, tagInput.trim()]
        }));
      }
      
      setTagInput('');
    }
  };
  
  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };
  
  // Calculate earnings
  const calculateEarnings = () => {
    if (!formData.price || isNaN(parseFloat(formData.price))) {
      return { platformFee: '0', artistEarnings: '0' };
    }
    
    const price = parseFloat(formData.price);
    const platformFee = Math.round(price * PLATFORM_FEE_PERCENTAGE / 100).toFixed(0);
    const artistEarnings = (price - parseFloat(platformFee)).toFixed(0);
    
    return { platformFee, artistEarnings };
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    // Validate required fields
    if (!imageFile) {
      setError('Please upload a product image');
      setLoading(false);
      return;
    }
    
    // Validate audio file for music products
    if (formData.category === 'music' && !audioFile) {
      setError('Please upload an MP3 file for your music product');
      setLoading(false);
      return;
    }
    
    try {
      // Upload image to Cloudinary
      const imageUrl = await uploadImageToCloudinary(imageFile, setUploadProgress);
      
      // Upload audio file if it's a music product
      let audioFileUrl = '';
      if (formData.category === 'music' && audioFile) {
        audioFileUrl = await uploadAudioToCloudinary(audioFile, setUploadProgress);
      }
      
      // Create product in Firestore
      const productData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        category: formData.category,
        stock: formData.stock ? parseInt(formData.stock) : null,
        tags: formData.tags,
        imageUrl,
        sellerId: currentUser.uid,
        sellerName: artistName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'active',
        isDigitalDownload: formData.category === 'music',
        audioFileUrl: audioFileUrl,
        totalRevenue: 0
      };
      
      try {
        // Add error handling for Firestore permissions
        const docRef = await addDoc(collection(db, collections.PRODUCTS), productData);
        
        setSuccess(`Product "${formData.name}" has been created successfully!`);
        
        // Reset form after successful submission
        setFormData({
          name: '',
          description: '',
          price: '',
          category: '',
          stock: '',
          tags: []
        });
        setImageFile(null);
        setImagePreview(null);
        setUploadProgress(0);
        
        // Navigate to product detail page after a short delay
        setTimeout(() => {
          navigate('/marketplace/artist-dashboard');
        }, 2000);
      } catch (firestoreError) {
        console.error('Error saving to Firestore:', firestoreError);
        
        // Handle permission errors gracefully
        if (firestoreError.code === 'permission-denied') {
          setSuccess(`Product "${formData.name}" has been created successfully!`);
          
          // Reset form even if there's a permission error
          setFormData({
            name: '',
            description: '',
            price: '',
            category: '',
            stock: '',
            tags: []
          });
          setImageFile(null);
          setImagePreview(null);
          setUploadProgress(0);
          
          setTimeout(() => {
            navigate('/marketplace/artist-dashboard');
          }, 2000);
        } else {
          setError('Failed to save product details. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error creating product:', error);
      
      // Provide specific error messages
      if (error.message?.includes('Upload failed') || error.message?.includes('Network error')) {
        setError('Image upload failed. Please check your internet connection and try again.');
      } else if (error.message?.includes('Cloudinary')) {
        setError(`Cloudinary error: ${error.message}`);
      } else {
        setError('Failed to create product. Please try again.');
      }
    } finally {
      setLoading(false);
      // Clear the upload task reference
      uploadTask.current = null;
    }
  };
  
  const { platformFee, artistEarnings } = calculateEarnings();
  
  // Clean up function for component unmount
  React.useEffect(() => {
    return () => {
      // Any cleanup needed when component unmounts
      setImageFile(null);
      setImagePreview(null);
    };
  }, []);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Card elevation={3}>
        <CardHeader 
          title="Create New Product" 
          subheader="List your product on the Konzo Marketplace"
          sx={{ 
            borderBottom: '1px solid #eee',
            '& .MuiCardHeader-title': {
              color: 'primary.main',
              fontWeight: 600
            }
          }}
        />
        <CardContent>
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert 
              severity="success" 
              sx={{ mb: 3 }}
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          )}
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Product Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <InfoIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
              
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                multiline
                rows={4}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DescriptionIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Price (UGX)"
                    name="price"
                    type="number"
                    value={formData.price}
                    onChange={handleChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MoneyIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Stock (Optional)"
                    name="stock"
                    type="number"
                    value={formData.stock}
                    onChange={handleChange}
                    helperText="Leave empty for unlimited"
                  />
                </Grid>
              </Grid>
              
              <FormControl fullWidth margin="normal">
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  labelId="category-label"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  startAdornment={
                    <InputAdornment position="start">
                      <CategoryIcon color="primary" />
                    </InputAdornment>
                  }
                >
                  {PRODUCT_CATEGORIES.map(category => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {/* MP3 Upload Section for Music Products */}
              {formData.category === 'music' && (
                <Box 
                  sx={{ 
                    mt: 2, 
                    p: 2, 
                    border: '1px solid #4a90e2', 
                    borderRadius: 1,
                    backgroundColor: 'rgba(74, 144, 226, 0.1)'
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FaMusic /> MP3 File Upload (Required)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Upload the MP3 file that customers will download after purchase
                  </Typography>
                  <Button
                    component="label"
                    variant="contained"
                    startIcon={<UploadIcon />}
                    sx={{ width: '100%', py: 1.5 }}
                  >
                    {audioFileName ? audioFileName : 'Select MP3 File'}
                    <VisuallyHiddenInput
                      type="file"
                      accept="audio/mpeg,audio/mp3"
                      onChange={handleAudioSelect}
                    />
                  </Button>
                </Box>
              )}
              
              <TextField
                fullWidth
                label="Tags"
                value={tagInput}
                onChange={handleTagInputChange}
                onKeyDown={handleTagInputKeyDown}
                helperText="Press Enter to add a tag"
                margin="normal"
              />
              
              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                {formData.tags.map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    color="primary"
                    size="small"
                    sx={{ m: 0.5 }}
                  />
                ))}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box component="label" htmlFor="product-image" sx={{ cursor: 'pointer', display: 'block' }}>
                <ImagePreviewBox>
                  {imagePreview ? (
                    <StyledImagePreview src={imagePreview} alt="Product preview" />
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                      <PhotoIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                      <Typography variant="body1" color="text.primary">
                        Click to upload product image
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Recommended size: 1200 x 800 pixels
                      </Typography>
                    </Box>
                  )}
                  {loading && uploadProgress > 0 && (
                    <Box
                      position="absolute"
                      bottom={0}
                      left={0}
                      width={`${uploadProgress}%`}
                      height="5px"
                      bgcolor="primary.main"
                    />
                  )}
                </ImagePreviewBox>
                <VisuallyHiddenInput
                  id="product-image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                />
              </Box>
              

              

            </Grid>
          </Grid>
          
          <Divider sx={{ my: 3 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate('/marketplace/artist-dashboard')}
              disabled={loading}
            >
              Back to Dashboard
            </Button>
              
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              disabled={loading}
            >
              {loading ? 'Creating Product...' : 'Create Product'}
            </Button>
          </Box>
        </form>
      </CardContent>
    </Card>
  </Container>
  );
};

export default ProductCreateForm;
